<template>
	<div :class="{
		'client-list': true,
		'client-list--empty': props.isCollapsed,
	}">
		<template v-if="!props.isCollapsed">
			<div class="client-list__filters">
				<div class="client-list__filters-row">
					<app-tooltip :text="createClientTitle">
						<template #activator>
							<app-icon
								class="client-list__filters-btn"
								name="plus"
								width="20px"
								color="#979797"
								:hover-color="createClientHoverColor"
								:cursor="createClientCursor"
								@click="onCreateClient"
							/>
						</template>
					</app-tooltip>
					<app-input
						v-model="clientSearch"
						class="client-list__filters-search"
						:placeholder="$t('search_by_clients')"
						show-search-button
						@press-enter="searchClients"
						@search="searchClients"
					/>
					<app-resizable-dropdown
						ref="$dropdownRef"
						position="right"
						:close-by-click-outside="true"
						:max-width="640"
						:max-height="436"
						:mobile-title="$t('choose_tags')"
						name="$dropdownRefClientsTags"
					>
						<template #activator>
							<app-tooltip :text="$t('ui_tag_filter')">
								<template #activator>
									<app-icon
										class="client-list__filters-btn"
										name="hash"
										width="20px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>

						<app-tag-selector
							v-model="selectedTags"
							:entity-type="TagEntityType.Client"
							mode="filter"
							allow-create
							@close="$dropdownRef?.close()"
						/>
					</app-resizable-dropdown>
				</div>
				<div
					v-if="selectedTags.length"
					class="client-list__filters-tags"
				>
					<div
						v-for="tag in selectedTags"
						class="client-list__filters-tags-item"
						:key="tag.Id"
					>
						<div class="client-list__filters-tags-item-name">
							{{ tag.Name }}
						</div>
						<app-icon
							class="client-list__filters-tags-item-delete"
							name="close-bold"
							size="12px"
							color="#D2D2D2"
							hover-color="#4D4D4D"
							cursor="pointer"
							@click.stop="dropTag(tag)"
						/>
					</div>
				</div>
				<div
					class="client-list__filters-counter"
					@click="emit('on-toggle')"
					@mouseenter="isCounterHovered = true"
					@mouseleave="isCounterHovered = false"
				>
					{{ $t(hasAnyFilter ? 'clients_found' : 'clients_total') }}:&nbsp;
					<span class="client-list__filters-counter-value">{{ clientsCount }}</span>
					<app-tooltip
						class="client-list__filters-counter-tooltip"
						:text="$t(props.isSecondCollapsed ? 'ui_shrink' : 'ui_expand')"
					>
						<template #activator>
							<app-icon
								class="client-list__filters-counter-icon"
								:name="props.isSecondCollapsed ? 'small-screen' : 'full-screen'"
								size="14px"
								:color="isCounterHovered ? '#438451' : '#979797'"
								hover-color="#438451"
								cursor="pointer"
							/>
						</template>
					</app-tooltip>
					<div
						v-if="hasAnyFilter"
						class="client-list__filters-counter-drop-filters"
						@click.stop="dropFilters"
					>
						{{ $t('selector_reset') }}
					</div>
				</div>
			</div>
			<div
				ref="$clientList"
				class="client-list__body"
				@scroll="onClientsScroll"
			>
				<app-loader
					v-if="clientsLoading"
					:style="loaderStyle"
				/>
				<template
					v-for="(client, index) in clients"
					:key="client.Id"
				>
					<client-card
						:client="client"
						:text-filter="clientSearch"
					/>
					<div
						v-if="index + 1 < clients.length"
						class="client-list__body-divider"
					/>
				</template>
				<app-scroll-top-button
					:enabled="isScrollTopEnabled"
					@on-click="scrollToTop"
				/>
			</div>
		</template>
		<app-tooltip
			v-else
			class="client-list__empty-tooltip"
			:text="$t('ui_expand')"
		>
			<template #activator>
				<div
					class="client-list__empty"
					@click="emit('on-toggle')"
					@mouseenter="isCounterHovered = true"
					@mouseleave="isCounterHovered = false"
				>
					{{ $t(hasAnyFilter ? 'clients_found' : 'clients_total') }}:&nbsp;
					<span class="client-list__empty-count">{{ clientsCount }}</span>
					<app-icon
						class="client-list__empty-icon"
						name="full-screen"
						width="14px"
						:color="isCounterHovered ? '#438451' : '#979797'"
						hover-color="#438451"
					/>
				</div>
			</template>
		</app-tooltip>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	onMounted,
	ref,
	watch
} from "vue";
import { useRoute, useRouter } from "vue-router";
import {searchStore} from "@/store/search";
import { headerStore } from "@/store/header";
import { ClientsApi } from "@/ api/ClientsApi";
import ClientCard from "@/components/main/search-panel/ClientCard.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppScrollTopButton from "@/components/ui-kit/button/AppScrollTopButton.vue";
import { useScrollTopButton } from "@/composables/useScrollTopButton";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { TagEntityType } from "@/models/etc/types";
import { i18n } from "@/i18n";

const t = (key: string): string => String(i18n.global.t(key))

const emit = defineEmits<{
	(e: 'on-toggle'): void
}>()

const props = defineProps<{
	isCollapsed: boolean,
	isSecondCollapsed: boolean
}>();

const store = searchStore()
const header = headerStore()
const route = useRoute()
const router = useRouter()
const $dropdownRef = ref<typeof AppResizableDropdown | null>(null)
const $clientList = ref<HTMLDivElement | null>(null)
const clientSearch = ref('')
const selectedTags = ref<TagWithFrequency[]>([])
const isCreatingClient = ref(false)
const isCounterHovered = ref(false)

const canCreateClient = computed(() => !!header.currentAccessGroup?.CanCreateClients)
const createClientTitle = computed(() =>
	t(canCreateClient.value ? 'client_create' : 'client_create_unavailable')
)
const createClientHoverColor = computed(() =>
	canCreateClient.value ? '#499259' : '#979797'
)
const createClientCursor = computed(() =>
	isCreatingClient.value ? 'wait' : (canCreateClient.value ? 'pointer' : 'not-allowed')
)

const clientsLoading = computed(() => store.clientsLoading || isCreatingClient.value)
const clientsCount = computed(() => store.clientsCounter)
const hasAnyFilter = computed(() => !!clientSearch.value || selectedTags.value.length > 0)
const clients = computed(() => store.allClientsHighlighted)
const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const { isScrollTopEnabled, scrollToTop } = useScrollTopButton(
	$clientList,
	'.searchResultContainer',
	() => clients.value
)

const onCreateClient = async () => {
	if (isCreatingClient.value) return
	if (!canCreateClient.value || header.currentAccessGroupId == null) return

	try {
		isCreatingClient.value = true
		const newId = await ClientsApi.createClient(header.currentAccessGroupId)
		if (newId) {
			await router.push({
				path: '/main',
				query: {
					page: 'client',
					id: String(newId),
				}
			})
		}
	} catch (e) {
		console.error(e)
	} finally {
		isCreatingClient.value = false
	}
}

const onClientsScroll = () => {
	const el = $clientList.value
	if (!el || clientsLoading.value) {
		return
	}
	if (el.scrollTop + el.clientHeight >= el.scrollHeight - 800) {
		store.getClientsPartial(clientSearch.value, selectedTags.value.map(tag => tag.Id))
	}
}

onMounted(async () => {
	await store.getInitClients()
})

const searchClients = async () => {
	$clientList.value ? $clientList.value.scrollTop = 0 : null
	await store.getClientsStartPack(clientSearch.value, selectedTags.value.map(tag => tag.Id))
}

const dropFilters = async () => {
	clientSearch.value = ''
	selectedTags.value = []
	await searchClients()
}

const dropTag = async (tag: TagWithFrequency) => {
	selectedTags.value = selectedTags.value.filter(sTag => sTag.Id !== tag.Id )
}

watch(clientSearch, (newVal, oldVal) => {
	if (!newVal && oldVal) {
		searchClients()
	}
})

watch(selectedTags, (newVal) => {
	if (!newVal.length) {
		searchClients()
	}
})
</script>

<style lang="scss" scoped>
.client-list {
	display: flex;
	flex-direction: column;
	background-color: #fff;
	border-radius: 8px;
	height: 100%;

	&:hover :deep(.app-scroll-top--enabled) {
		opacity: 1;
		visibility: visible;
		pointer-events: auto;
	}

	:deep(.app-scroll-top) {
		margin-right: 0;
	}

	@media (max-width: 480px) {
		:deep(.app-scroll-top-tooltip) {
			bottom: calc(6px + env(safe-area-inset-bottom, 0px));
		}

		:deep(.app-scroll-top) {
			position: static;
			right: auto;
		}
	}

	&--empty {
		justify-content: center;
	}

	/* Полоса-счётчик кликается целиком, поэтому обёртки тултипа не должны её сжимать. */
	&__empty-tooltip,
	&__empty-tooltip :deep(.app-tooltip__activator) {
		display: block;
		width: 100%;
	}

	&__empty {
		display: flex;
		align-items: center;
		justify-content: center;
		color: #4D4D4D;
		font-size: 12px;
		line-height: 16px;
		cursor: pointer;

		&-count {
			font-size: 12px;
			line-height: 16px;
			font-weight: 500;
			margin-right: 2px;
		}

		&-icon {
			margin-top: -2px;
		}
	}

	&__filters {
		display: flex;
		flex-direction: column;
		padding: 8px;

		&-counter {
			display: flex;
			align-items: center;
			justify-content: center;
			position: relative;
			font-size: 12px;
			line-height: 16px;
			font-weight: 400;
			color: #4D4D4D;
			cursor: pointer;

			&-value {
				font-size: 12px;
				line-height: 16px;
				font-weight: 500;
				margin-right: 2px;
			}

			&-icon {
				margin-top: -2px;
			}

			&-tooltip,
			&-tooltip :deep(.app-tooltip__activator) {
				display: flex;
			}

			&-drop-filters {
				position: absolute;
				right: 0;
				bottom: 0;
				font-size: 12px;
				line-height: 16px;
				color: #979797;
				cursor: pointer;

				&:hover {
					text-decoration: underline;
				}
			}
		}

		&-row {
			display: flex;
			align-items: center;
			color: #4D4D4D;
			margin-bottom: 8px;

			&:last-child {
				margin-bottom: 0;
			}

			&--center {
				justify-content: center;
			}

			.expand-icon {
				font-size: 20px;
			}
		}

		&-search {
			width: 100%;
			margin: 0 4px;
		}

		&-tags {
			display: flex;
			flex-wrap: wrap;
			margin-bottom: 4px;
			gap: 4px;

			&-item {
				display: flex;
				align-items: center;
				text-transform: lowercase;

				&-name {
					color: #979797;
					font-size: 14px;
					line-height: 18px;
				}
			}
		}
	}

	&__body {
		display: flex;
		flex-direction: column;
		flex: 1;
		position: relative;
		padding: 8px;
		border-top: 1px solid #F2F2F2;
		overflow: auto;
	}

	&__body-divider {
		display: flex;
		width: 100%;
		border-top: 1px solid #F2F2F2;
		margin: 8px 0;
	}

	&::v-deep(.q-field__control) {
		height: 32px;
	}
}
</style>
